import {observable, action} from 'mobx';
import {message} from '@enos/dpl';
import localeText from '@locale';
import Pagination from './Pagination';
import uris from '~/uris';
import User from './models/User';
import Role from './models/Role';
import UserGroup from './models/UserGroup';
import Structure from './models/Structure';
import moment from 'moment';
import {STATUS_CODE, USER_PERMISSION_TYPE} from '~/utils/constants';
import axiosInstance, {downloadFileWithPost} from '~/utils/axiosInstance';

export class UserStore extends Pagination {
  searchText = '';

  @observable users = [];

  @observable currentUser = {};

  @observable isLoading = false;

  @observable assignedRoleLists = [];

  @observable assignedUserGroupLists = [];

  @observable assignedStructureLists = [];

  @observable withAssignRoles = [];

  @observable WithAssignUserGroups = [];

  @observable structureChildren = [];

  @observable selectStructures = [];

  @observable initSelectedUserStructures = [];

  @observable usersLength;

  @observable initStartTime = '';

  @observable initExpireTime = '';

  @observable allUsers = [];

  @observable exportLoading = false;

  @observable userListFilter = [];

  @observable isShowCanpany = false;

  @observable defaultUser = {};

  @action.bound
  setDefaultUser(data) {
    this.defaultUser = data;
  }

  @action
  setUsersLength(num) {
    this.usersLength = num;
  }

  @action.bound
  setUserFilterList(list) {
    this.userListFilter = list;
  }

  @action.bound
  getUserList(body = {}) {
    this.isLoading = true;
    axiosInstance
      .post(uris.getUserList(), {
        ...body
      })
      .then((data) => {
        this.setCurrentUser((data.users || [])[0]);
        this.setUsers(data.users);
        this.setPagination(data.pagination);
        this.setUsersLength((data.users || []).length);
        if (data.users && data.users.length) {
          this.setDefaultUser(data.users[0]);
        }
        this.setLoading();
      })
      .catch(() => this.setLoading());
  }

  @action('获取用户列表with 搜索和分页')
  getUserListWithAddition(body = {}) {
    let data = {
      ...this.paginationRequestBody
    };

    if (this.usersLength === 1 && body.isDelete) {
      data = {
        ...this.paginationRequestBodyBefore
      };
    }

    if (body.forceFresh) {
      this.searchText = '';
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.getUserList({
      search: this.searchText.trim(),
      sortType: '',
      filter: [],
      sortBy: 'createdTime',
      ...data,
      ...body
    });
  }

  @action.bound
  onSearch(value = '') {
    this.searchText = value;
    this.getUserListWithAddition(this.initPaginationRequestBody);
  }

  @action('设置loading')
  setLoading(boolean) {
    this.isLoading = boolean;
  }

  @action.bound
  changePagination(pagination) {
    super.changePagination(pagination);
  }

  @action.bound
  async getUserDetail(id) {
    this.setLoading(true);
    return axiosInstance
      .get(uris.getUserDetail(), {
        params: {id}
      })
      .then((data) => {
        this.setCurrentUser(data);
        this.setLoading(false);
      });
  }

  @action.bound
  setCurrentUser(user) {
    this.currentUser = user;
    this.setInitStartTime(user?.accountStartTime);
    this.setInitExpireTime(user?.accountExpireTime);
  }

  @action.bound
  setInitStartTime(time) {
    this.initStartTime = time && moment.utc(time).local().startOf('day').format('YYYY-MM-DD HH:mm:ss');
  }

  @action.bound
  setInitExpireTime(time) {
    this.initExpireTime = time && moment.utc(time).local().startOf('day').format('YYYY-MM-DD HH:mm:ss');
  }

  @action.bound
  triggerResetPassword(email) {
    return axiosInstance
      .post(uris.triggerResetPassword(), null, {
        params: {email}
      })
      .then(() => {
        message.success(localeText('USER.MSG_TRIGGER_RESET_CIPHER'));
      });
  }

  @action.bound
  triggerRetrievePassword(email) {
    return axiosInstance
      .post(uris.triggerRetrievePassword(), null, {
        params: {email},
        skipNotFound: true
      })
      .then(() => {
        message.success(localeText('USER.MSG_TRIGGER_RESET_CIPHER'));
      })
      .catch(() => Promise.reject());
  }

  @action('邀请已有用户加入组织')
  inviteUser({userId, organizationId}) {
    this.isLoading = true;
    /* 添加 data 参数的验证逻辑 */
    return axiosInstance
      .post(uris.userOrganizationJoin(), {
        userId,
        organizationId
      })
      .catch(() => this.setLoading(false) && Promise.reject());
  }

  @action('创建并且邀请')
  userCreateAndJoin(data) {
    this.isLoading = true;
    return axiosInstance.post(uris.userOrganizationCreateAndJoin(), data);
  }

  @action('验证是否存在')
  validateUser(value) {
    return axiosInstance.post(uris.validateUser(), null, {
      params: {
        field: 'name',
        value
      }
    });
  }

  @action('更新用户信息')
  updateUser(data) {
    /* 添加数据验证的逻辑 */
    axiosInstance.post(uris.updateUser(), data).then((json) => {
      this.updateUserFromServer(json);
    });
  }

  @action('清除已加载的用户缓存')
  setUsers(data) {
    this.users = data;
  }

  @action.bound
  setAllUsers(data) {
    this.allUsers = data;
  }

  @action('set')
  setassignedRoleLists(data) {
    this.assignedRoleLists = data;
  }

  @action('移除用户')
  removeUser(id) {
    this.isLoading = true;
    axiosInstance
      .post(
        uris.removeUser(),
        {
          userId: id
        },
        {
          params: {
            userId: id
          },
          showError: {
            showPopup: true,
            title: localeText('ROLE.TITLE_DELETE_FAIL')
          }
        }
      )
      .then(() => {
        message.success(localeText('USER.MSG_REMOVE_SUCCESS'));
        this.getUserListWithAddition({isDelete: true});
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action('添加单个用户数据')
  updateUserFromServer(json) {
    let user = this.users.find(({id}) => id === json.id);
    if (!user) {
      user = new User(this, json.id);
    }
    user.updateFromJson(json);
    return user;
  }

  @action.bound
  resetPassword(body = {}) {
    return axiosInstance.post(uris.resetPassword(), body);
  }

  @action('modifyuser')
  modifyUser(id, nickName, phoneArea, phone, company, department, position, accountStartTime, accountExpireTime) {
    return axiosInstance.post(uris.modifyUser(), {
      id, nickName, phoneArea, phone, company, department, position, accountStartTime, accountExpireTime
    });
  }

  @action('获取列表')
  getAssignRoleList(userId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.getAssignRoleList(), null, {
        params: {userId}
      })
      .then((data) => {
        this.setassignedRoleLists(data.roles.map(json => this.updateAssignedRoleFromServer(json)));
        this.setLoading(false);
      })
      .catch(() => this.setLoading(false));
  }

  @action('添加单个数据')
  updateAssignedRoleFromServer(json) {
    let role = this.assignedRoleLists.find(({id}) => id === json.id);
    if (!role) {
      role = new Role(this, json.id);
    }

    role.updateFromJson(json);
    return role;
  }

  @action('delete')
  removeUserRole(userId, roleId) {
    return axiosInstance.post(uris.removeUserRole(), null, {params: {userId, roleId}});
  }

  @action('获取用户列表')
  async getWithAssignRoles(userId) {
    this.isLoading = true;
    let data;
    try {
      data = await axiosInstance.post(uris.getWithAssignRoles(), {userId});
      this.setWithAssignRoles(data.roles.map(json => this.updateAssignedRoleFromServer(json)));
    } finally {
      this.setLoading(false);
    }
    return data.roles;
  }

  @action('set')
  setWithAssignRoles(data) {
    this.withAssignRoles = data;
  }

  @action('set')
  async assignUserRoles(roleIds) {
    await axiosInstance.post(uris.assignUserRoles(), {userId: this.currentUser.id, roleIds});
  }

  @action('列表')
  async getWithAssignUserGroups(userId) {
    this.isLoading = true;
    let data;
    try {
      data = await axiosInstance.post(uris.getWithAssignUserGroups(), {userId});
      this.setWithAssignUserGroups(data.userGroups);
    } finally {
      this.setLoading(false);
    }

    return data.userGroups;
  }

  @action('set')
  setWithAssignUserGroups(data) {
    this.WithAssignUserGroups = data;
  }

  @action('获取列表')
  getAssignedUserGroupLists(userId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.getAssignedUserGroupLists(), null, {
        params: {userId}
      })
      .then((data) => {
        this.setAssignedUserGroupLists(data.userGroups.map(json => this.updateAssignedUserGroupFromServer(json)));
        this.setLoading(false);
      })
      .catch(() => this.setLoading(false));
  }

  @action('set')
  setAssignedUserGroupLists(data) {
    this.assignedUserGroupLists = data;
  }

  @action('添加单个数据')
  updateAssignedUserGroupFromServer(json) {
    let userGroup = this.assignedUserGroupLists.find(({id}) => id === json.id);
    if (!userGroup) {
      userGroup = new UserGroup(this, json.id);
    }
    userGroup.updateFromJson(json);
    return userGroup;
  }

  @action('delete')
  removeUserUserGroup(userId, userGroupId) {
    return axiosInstance.post(uris.removeUserGroup(), null, {params: {userId, userGroupId}});
  }

  @action('set')
  async assignUserUserGroups(userGroupIds) {
    await axiosInstance.post(uris.assignUserUserGroups(), {userId: this.currentUser.id, userGroupIds});
  }

  @action('获取列表')
  getAssignStructureList(userId, userPermissionType) {
    this.isLoading = true;
    return axiosInstance
      .post(uris.getAssignStructureList(), null, {
        params: {userId},
        skipGlobalHandler: true // 跳过全局404处理
      })
      .then((data) => {
        if ((data.code === STATUS_CODE.PERMISSION_DENIED || data.code === STATUS_CODE.NOT_FOUND)
        && userPermissionType === USER_PERMISSION_TYPE.SUB_ADMIN) {
          window.location.href = '/admin/users';
          return;
        }
        this.setAssignedStructureLists(data.structures.map(json => this.updateAssignedStructuresFromServer(json)));
        this.setSelectedUserStructuresFetch((data || {}).structures);
        this.setLoading(false);
        return data;
      })
      .catch(() => this.setLoading(false));
  }

  @action('set')
  setAssignedStructureLists(data) {
    this.assignedStructureLists = data;
  }

  @action('添加单个数据')
  updateAssignedStructuresFromServer(json) {
    let structure = this.assignedStructureLists.find(({id}) => id === json.id);
    if (!structure) {
      structure = new Structure(this, json.id, json.name);
    }
    structure.updateStructurePathFromJson(json);
    return structure;
  }

  @action('modifyPassword')
  modifyPassword(oldPassword, newPassword, keyId) {
    this.isLoading = true;
    return axiosInstance
      .post(uris.modifyPassword(), {oldPassword, newPassword, keyId})
      .then(() => {
        message.success(localeText('USER.MSG_MODIFY_CIPHER_SUCCESS'));
        this.setLoading(false);
      })
      .catch(() => {
        this.setLoading(false);
        return Promise.reject();
      });
  }

  @action('validate token info')
  getResetTokenInfo(token) {
    return axiosInstance.post(uris.getResetTokenInfo(), null, {
      params: {token}
    });
  }

  @action('delete')
  removeStructure(userId, structureId) {
    return axiosInstance.post(uris.removeStructure(), null, {params: {userId, structureId}});
  }

  @action.bound
  modifyUserProfile(theme) {
    this.setLoading(true);
    return axiosInstance.post(uris.modifyUserProfile(), {theme}).then(() => {
      this.setLoading(false);
    });
  }

  @action.bound
  setSelectedUserStructuresFetch(data) {
    this.initSelectedUserStructures = data;
    // this.initSeuserDownloades = data;
  }

  @action
  setExportLoading(boolean) {
    this.exportLoading = boolean;
  }

  @action.bound
  userExportDownload(taskId) {
    return downloadFileWithPost(uris.userExportDownload(), {taskId})
      .then((response) => {
        this.downloadFile(
          response,
          `${localeText('APP.TITLE_DOWNLOAD_NAME')}_${new Date().toISOString().substring(0, 10)}`,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
        this.setExportLoading(false);
      })
      .catch(() => {
        this.setExportLoading(false);
      });
  }

  // 下载文件，传入data，文件名和文件类型
  downloadFile(res, filename, type) {
    const blob = new Blob([res], {type});
    if (window.navigator.msSaveBlob) { // IE
      window.navigator.msSaveOrOpenBlob(blob, `${filename}`);
    }
    const fileURL = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = fileURL;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(fileURL);
  }

  @action.bound
  exportUser() {
    return axiosInstance
      .get(uris.exportUser());
  }

 @action.bound
  getExportStatus(taskId) {
    return axiosInstance
      .post(uris.getExportStatus(), {taskId});
  }

  @action.bound
 setShowCampany(data) {
   this.isShowCanpany = data;
 }

  @action.bound
  getDisplayConfig() {
    axiosInstance
      .post(uris.getDisplayConfig())
      .then((data) => {
        this.setShowCampany(data);
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

//   @action.bound
//  getUserFilterList() {
//    this.isLoading = true;
//    return axiosInstance
//      .get(
//        uris.getUserFilterList()
//      )
//      .then((data) => {
//        this.setUserFilterList(data);
//        this.setLoading(false);
//      })
//      .catch(() => {
//        this.setLoading(false);
//      });
//  }
}

export default new UserStore();
